import { itemMatchesQuery, normalizeLimit } from "./search-helpers.js";

// Filters by the item's search strings. Passes through when noFilter or query
// is empty. Under a query a header row passes only when a matched item follows
// it before the next header. When `maxResults` is set, stops after
// that many matched (non-header) items so a broad query over a large list never
// renders thousands of rows synchronously. Pinned rows bypass matching, always
// render last, and reserve their slots within the cap — so a pinned action row
// survives even a broad query that fills the cap. `fallback: true` rows are an
// empty-state affordance: excluded from matching, rendered only when the query
// matches nothing else.
export function filterBySearch(...args) {
  return filterSearchResults(...args).items;
}

/** Returns rendered rows and whether any ordinary item matched before capping. */
export function filterSearchResults(items, search, noFilter, getItemTitle, maxResults) {
  const hasQuery = (search?.length ?? 0) > 0;
  if (noFilter || !hasQuery) {
    const visibleItems = items.some((item) => item?.fallback) ? items.filter((item) => !item?.fallback) : items;
    return {
      items: visibleItems,
      hasMatches: items.some((item) => !item?.header && !item?.pinned && !item?.fallback),
    };
  }

  const pinned = items.filter((item) => item?.pinned);
  const query = search.toLowerCase();
  const limit = normalizeLimit(maxResults);
  const cap = limit === null ? null : Math.max(0, limit - pinned.length);
  const matches = [];
  let matched = 0;
  let hasMatches = false;

  for (const item of items) {
    if (item?.header) {
      matches.push(item);
      continue;
    }
    if (item?.pinned || item?.fallback) {
      continue;
    }
    if (itemMatchesQuery(item, query, getItemTitle)) {
      hasMatches = true;
      if (cap !== null && matched >= cap) {
        break;
      }
      matches.push(item);
      matched++;
    }
  }

  const fallbackRows = matched === 0 ? items.filter((item) => item?.fallback) : [];
  return { items: [...withoutEmptyHeaders(matches), ...fallbackRows, ...pinned], hasMatches };
}

function withoutEmptyHeaders(rows) {
  return rows.filter((row, index) => !row?.header || (index + 1 < rows.length && !rows[index + 1]?.header));
}
